import React from 'react';
import { connect } from 'react-redux';
import { RefCollection } from '../../../FieldTypes/Reference';

import Header from './Header';
import Row from './Row';
import ListItem from './ListItem';

import { IRootState } from '../../../../modules/stores';
import { IRecordById, IFieldById } from '../../../../types/core';
import { ITableConfig, ListStyleType, BorderType } from '../../../../types/space';

import { BORDER_COMPONENT, LIST_COMPONENT } from './styled';
import { mapPropertyToIdArray, isAbsentCellValue } from '../../../../helpers';

type IConnectProps = ReturnType<typeof mapState>;
type IConnectPropsLookup = ReturnType<typeof mapStateLookup>;
type IConnectPropsText = ReturnType<typeof mapStateText>;
interface IProps {
  // ID is for grabbing documentById when unfoldingShadow elements
  id?: string;
  recordId: string;
  fieldId: string;
  config: ITableConfig;
  depth?: number;
  spaceId: string;
}
interface IPropsLookup {
  id?: string;
  recordId: string;
  lookupValue: Array<{ id: string }>;
  config: ITableConfig;
  depth?: number;
  spaceId: string;
}
interface IPropsText {
  recordId: string;
  fieldId: string;
}
interface ITableRawProps {
  id?: string;
  records: IRecordById[];
  fields: IFieldById[];
  fieldById: { [key: string]: IFieldById };
  config: ITableConfig;
  depth?: number;
  spaceId: string;
}
interface IListRawProps {
  id?: string;
  records: IRecordById[];
  fields: IFieldById[];
  config: ITableConfig;
  depth?: number;
  spaceId: string;
}

/**
 * Don't be afraid of this file. Basically theres a table component, and a list component.
 * And for both of them, theres a lookup version and a normal version. The difference between the
 * lookup version and the normal version is the props.
 *
 * Lookup - passes a lookupValue prop which is an array of the records.
 * Normal - needs to get the array of records by getting the cell. Take a gander at `mapState`
 */
const TableRaw = ({ config, fields, fieldById, id, records, depth, spaceId }: ITableRawProps) => {
  const Component =
    BORDER_COMPONENT[config.borderStyle as BorderType] || BORDER_COMPONENT[BorderType.All];
  return (
    // @ts-ignore
    <Component headingStyle={config.headingStyle} rowStyle={config.rowStyle} id={id}>
      {config.showHeadings && (
        <thead>
          <Header
            fieldById={fieldById}
            tableColumns={config.tableColumns}
            showRefAsText={config.showRefAsText}
            depth={depth ? depth + 1 : 1}
          />
        </thead>
      )}
      <tbody>
        {records.map((record: IRecordById, index: number) => (
          <Row
            key={`row-${depth ? depth : 1}-${index}`}
            fields={fields}
            record={record}
            tableColumns={config.tableColumns}
            showRefAsText={config.showRefAsText}
            depth={depth ? depth + 1 : 1}
            spaceId={spaceId}
          />
        ))}
      </tbody>
    </Component>
  );
};

class TableWithFieldId extends React.PureComponent<IProps & IConnectProps> {
  public render() {
    const { cellValue, fieldById, id, recordById, config, depth, spaceId } = this.props;
    if (isAbsentCellValue(cellValue) || !Array.isArray(cellValue)) return null;

    const refFields = mapPropertyToIdArray(config.tableColumns, fieldById);
    const refRecords = mapPropertyToIdArray(cellValue, recordById);

    return (
      <TableRaw
        config={config}
        fields={refFields}
        fieldById={fieldById}
        records={refRecords}
        id={id}
        depth={depth}
        spaceId={spaceId}
      />
    );
  }
}

class TableForLookup extends React.PureComponent<IPropsLookup & IConnectPropsLookup> {
  public render() {
    const { cellValue, fieldById, recordById, lookupValue, id, config, depth, spaceId } =
      this.props;
    if (isAbsentCellValue(cellValue) || !Array.isArray(cellValue)) return null;

    const refFields = mapPropertyToIdArray(config.tableColumns, fieldById);
    const refRecords = mapPropertyToIdArray(lookupValue, recordById);

    return (
      <TableRaw
        config={config}
        fields={refFields}
        fieldById={fieldById}
        records={refRecords}
        id={id}
        depth={depth}
        spaceId={spaceId}
      />
    );
  }
}

const ListRaw: React.FC<IListRawProps> = ({ config, fields, id, records, depth, spaceId }) => {
  const Component = LIST_COMPONENT[config.listStyle as ListStyleType];
  return (
    // @ts-ignore
    <Component rowStyle={config.rowStyle} id={id}>
      {records.map((record: IRecordById, index: number) => (
        <ListItem
          key={`row-${depth ? depth : 1}-${index}`}
          fields={fields}
          record={record}
          tableColumns={config.tableColumns}
          listStyle={config.listStyle}
          showRefAsText={config.showRefAsText}
          depth={depth ? depth + 1 : 1}
          spaceId={spaceId}
        />
      ))}
    </Component>
  );
};

class ListWithFieldId extends React.PureComponent<IProps & IConnectProps> {
  public render() {
    const { cellValue, fieldById, recordById, config, id, depth, spaceId } = this.props;
    if (isAbsentCellValue(cellValue) || !Array.isArray(cellValue) || !config.tableColumns)
      return null;

    const refFields = mapPropertyToIdArray(config.tableColumns, fieldById);
    const refRecords = mapPropertyToIdArray(cellValue, recordById);

    return (
      <ListRaw
        records={refRecords}
        fields={refFields}
        config={config}
        id={id}
        depth={depth}
        spaceId={spaceId}
      />
    );
  }
}

class ListForLookup extends React.PureComponent<IPropsLookup & IConnectPropsLookup> {
  public render() {
    const { cellValue, fieldById, recordById, lookupValue, id, config, depth, spaceId } =
      this.props;
    if (isAbsentCellValue(cellValue) || !Array.isArray(cellValue)) return null;

    const refFields = mapPropertyToIdArray(config.tableColumns, fieldById);
    const refRecords = mapPropertyToIdArray(lookupValue, recordById);

    return (
      <ListRaw
        records={refRecords}
        fields={refFields}
        config={config}
        id={id}
        depth={depth}
        spaceId={spaceId}
      />
    );
  }
}

const TextWithFieldId = ({ cellValue }: IConnectPropsText) => (
  <RefCollection foreignRows={cellValue || []} />
);

const TextForLookup = ({ foreignRow }: any) => <RefCollection foreignRows={foreignRow} />;

const mapState = ({ core }: IRootState, { recordId, fieldId }: IProps) => ({
  cellValue: core.recordById[recordId]?.cells[fieldId],
  fieldById: core.fieldById,
  recordById: core.recordById,
});

const mapStateLookup = ({ core }: IRootState, { recordId, fieldId }: IPropsText) => ({
  cellValue: core.recordById[recordId || core.currentRecordId]?.cells[fieldId],
  fieldById: core.fieldById,
  recordById: core.recordById,
});

const mapStateText = ({ core }: IRootState, { recordId, fieldId }: IPropsText) => ({
  cellValue: core.recordById[recordId || core.currentRecordId]?.cells[fieldId],
});

export default {
  TableWithFieldId: connect(mapState)(TableWithFieldId),
  TableForLookup: connect(mapStateLookup)(TableForLookup),
  ListWithFieldId: connect(mapState)(ListWithFieldId),
  ListForLookup: connect(mapStateLookup)(ListForLookup),
  TextWithFieldId: connect(mapStateText)(TextWithFieldId),
  TextForLookup: TextForLookup,
};
